import * as Sentry from '@sentry/react';
import { env } from '@/config/env';
import { readRuntimeConfig } from '@/config/runtime';
import { setContractDriftReporter, type ContractDrift } from '@/api/contract-drift';

/**
 * The panel's telemetry, which existed on paper and nowhere else.
 *
 * `@sentry/react` has been a dependency and `VITE_SENTRY_DSN` has been in the
 * env contract for months, and nothing ever called `Sentry.init`. So the panel
 * has been the one surface of this platform with no way of telling anybody
 * anything: the app reports, the backend is wired for it, and an operator
 * hitting a broken screen had the browser console or nothing.
 *
 * What made that worst was `reportContractDrift`. `http.ts` detects a response
 * that does not match the schema and calls it; its default sink is
 * `console.error`; and `setContractDriftReporter` — whose own docstring says
 * "Sentry in the browser" — was only ever called from tests. A backend field
 * that changes shape makes a read fail with `CONTRACT_MISMATCH` and a write
 * pass degraded, and the only person who could find out was somebody who
 * happened to have devtools open.
 *
 * And once `Sentry.init` was here, it was still mute: `VITE_SENTRY_DSN` was a
 * build ARG with an empty default that nobody passed in Dokploy, so every
 * production build compiled green with `dsn: ''`. The DSN now comes from
 * `/config.js`, which the container writes from its own env at start-up (see
 * `src/config/runtime.ts`); the build arg is kept as a fallback.
 */

/** Headers and query keys that are credentials, or carry one. */
const SECRET_KEYS = ['authorization', 'cookie', 'token', 'password', 'secret', 'apikey'];

function looksSecret(name: string): boolean {
  const k = name.toLowerCase().replace(/[-_]/g, '');
  return SECRET_KEYS.some((s) => k.includes(s));
}

/**
 * Strip anything that could carry a session before an event leaves the browser.
 *
 * The panel is the tool that moves other people's money, so its errors travel
 * next to admin sessions and the identifiers of the users being acted on. The
 * request body is dropped whole rather than field by field: a redaction list
 * has to be right about every future endpoint, and it is wrong silently.
 */
function scrub(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  const req = event.request;
  if (req) {
    delete req.data;
    delete req.cookies;
    if (req.headers) {
      for (const name of Object.keys(req.headers)) {
        if (looksSecret(name)) delete req.headers[name];
      }
    }
    if (req.query_string && typeof req.query_string === 'object') {
      for (const name of Object.keys(req.query_string)) {
        if (looksSecret(name)) delete (req.query_string as Record<string, unknown>)[name];
      }
    }
  }
  return event;
}

/**
 * Idempotent: `main.tsx` calls it once, and a second call is a no-op rather
 * than a second client. React 18 StrictMode double-invokes effects in dev.
 */
let started = false;

export function initObservability(): void {
  if (started) return;
  started = true;

  // The drift reporter is wired even with no DSN. `Sentry.captureMessage` is a
  // no-op on an uninitialised client, so this costs nothing and means the day
  // somebody sets the DSN they do not also have to remember this line.
  setContractDriftReporter(reportDriftToSentry);

  // Runtime first, build arg second. The container knows which deployment it
  // is; the bundle is the same file in every one of them.
  const runtime = readRuntimeConfig();
  const dsn = runtime.sentryDsn ?? env.VITE_SENTRY_DSN;
  if (!dsn) return;

  // Not `import.meta.env.MODE`: that is `production` for every Vite build, so
  // staging and production landed under one environment and could not be told
  // apart. `VITE_ENV` is the build-time answer; the runtime file overrides it.
  const environment = runtime.environment ?? env.VITE_ENV;

  // `main.tsx` calls this before `createRoot`, and the DSN is now a value an
  // operator types into Dokploy. A bad one must cost telemetry, not the mount.
  try {
    Sentry.init({
      dsn,
      environment,
      // The panel has a handful of operators, not traffic. Sampling it down would
      // trade the only signal there is for savings measured in cents.
      tracesSampleRate: 0,
      sendDefaultPii: false,
      beforeSend: scrub,
    });
  } catch (err) {
    console.error('[observability] Sentry.init failed; the panel runs without it', err);
  }
}

/**
 * A drift is one issue per endpoint, not one per occurrence.
 *
 * The fingerprint is method + path + status and deliberately excludes the zod
 * issues: a field that changed shape produces a different issue list depending
 * on which rows came back, and grouping by that would split one backend change
 * into a dozen issues. The path already has its ids collapsed by `http.ts`.
 *
 * `warning`, not `error`: the panel usually keeps working, and an operator is
 * not blocked. What this buys is finding out on the day the backend changed
 * instead of on the day a screen goes blank.
 */
function reportDriftToSentry(drift: ContractDrift): void {
  Sentry.captureMessage(`contract drift ${drift.method} ${drift.path}`, {
    level: 'warning',
    fingerprint: ['contract-drift', drift.method, drift.path, String(drift.status)],
    extra: {
      status: drift.status,
      degraded: drift.degraded,
      issues: drift.issues,
    },
  });
  // Kept alongside, not replaced: somebody debugging with devtools open should
  // still see it there, and the console is the only sink when there is no DSN.
  console.error(
    `[api] contract drift ${drift.method} ${drift.path} (${drift.status})`,
    drift.issues,
  );
}

/** Exported for the test; not part of the module's contract. */
export const __scrubForTest = scrub;
